/**
 * Phase 8 T-18 §26.C — Feedback modal.
 *
 * 420 × 380 modal with:
 *   - Textarea "What's on your mind?"
 *   - Optional email field
 *   - Attach screenshot toggle (html2canvas — already a dep)
 *   - [Send] emerald button
 *
 * POSTs to /api/feedback. On 404 / any non-2xx, logs to console and
 * shows a toast "Thanks — we've recorded your feedback locally."
 * TODO(§8-v2): wire feedback endpoint when the backend route exists.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Camera, CameraOff, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

type Status = 'idle' | 'capturing' | 'submitting' | 'done';

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { showToast } = useGlobalToast();
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [attachScreenshot, setAttachScreenshot] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 60);
    } else {
      setMessage('');
      setEmail('');
      setAttachScreenshot(false);
      setScreenshotDataUrl(null);
      setStatus('idle');
    }
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const captureScreenshot = useCallback(async () => {
    setStatus('capturing');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        allowTaint: false,
        // Restrict to current viewport only — avoids leaking OS/other tabs.
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: 0,
        y: window.scrollY,
      });
      setScreenshotDataUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      console.warn('[FeedbackModal] Screenshot capture failed:', err);
      setScreenshotDataUrl(null);
    } finally {
      setStatus('idle');
    }
  }, []);

  const handleScreenshotToggle = useCallback(() => {
    const next = !attachScreenshot;
    setAttachScreenshot(next);
    if (next) {
      void captureScreenshot();
    } else {
      setScreenshotDataUrl(null);
    }
  }, [attachScreenshot, captureScreenshot]);

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return;
    setStatus('submitting');

    const payload = {
      message: message.trim(),
      email: email.trim() || undefined,
      screenshot: attachScreenshot && screenshotDataUrl ? screenshotDataUrl : undefined,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    let submitted = false;
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) submitted = true;
    } catch {
      // network error — fall through to local-log path
    }

    if (!submitted) {
      // TODO(§8-v2): wire feedback endpoint when backend route exists.
      console.info('[FeedbackModal] Feedback recorded locally:', payload);
      showToast({
        variant: 'success',
        title: "Thanks — we've recorded your feedback locally.",
      });
    } else {
      showToast({ variant: 'success', title: 'Thanks for your feedback!' });
    }

    setStatus('done');
    onClose();
  }, [message, email, attachScreenshot, screenshotDataUrl, showToast, onClose]);

  if (!open) return null;

  const isCapturing = status === 'capturing';
  const isSubmitting = status === 'submitting';
  const busy = isCapturing || isSubmitting;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-[1px]"
        aria-hidden
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Give feedback"
        className="fixed left-1/2 top-1/2 z-[201] flex -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border shadow-2xl vscode-border vscode-bg-elevated"
        style={{ width: 420, maxWidth: 'calc(100vw - 2rem)' }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 vscode-border">
          <h2 className="text-[14px] font-semibold vscode-text-primary">Give feedback</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            aria-label="Close feedback modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-3 px-4 py-4" style={{ minHeight: 0 }}>
          {/* Message textarea */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind?"
            disabled={busy}
            rows={5}
            className="w-full resize-none rounded-[8px] border px-3 py-2 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[#10B981] disabled:opacity-60"
          />

          {/* Email (optional) */}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email (optional)"
            disabled={busy}
            className="w-full rounded-[8px] border px-3 py-2 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[#10B981] disabled:opacity-60"
          />

          {/* Screenshot toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleScreenshotToggle}
              disabled={busy}
              className="flex items-center gap-2 rounded-[6px] px-2 py-1 text-[12px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary disabled:opacity-60"
              aria-pressed={attachScreenshot}
            >
              {attachScreenshot ? (
                <Camera className="h-3.5 w-3.5 text-[#10B981]" />
              ) : (
                <CameraOff className="h-3.5 w-3.5" />
              )}
              {isCapturing ? 'Capturing…' : attachScreenshot ? 'Screenshot attached' : 'Attach screenshot'}
            </button>

            {screenshotDataUrl && (
              <img
                src={screenshotDataUrl}
                alt="Screenshot preview"
                className="h-8 w-14 rounded border object-cover vscode-border"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3 vscode-border">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-[8px] px-4 py-1.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={busy || !message.trim()}
            className="flex items-center gap-2 rounded-[8px] bg-[#10B981] px-4 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Sending…
              </>
            ) : (
              'Send'
            )}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
